import { faPaperclip } from '@fortawesome/pro-regular-svg-icons/faPaperclip';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons/faArrowUp';
import { faCircleStop } from '@fortawesome/pro-light-svg-icons/faCircleStop';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SxProps, Theme } from '@mui/material';
import { QBox, QButton, QChip, QIcon, QTextField } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import { agentIds } from '@captivateiq/assistant/config/agentConfig';
import { validateFileType } from '@captivateiq/assistant/services/AttachmentService';
import type { AttachmentMeta } from '@captivateiq/assistant/types';
import {
  FEATURE_FLAG_AI_SHELL_FILE_UPLOAD,
  FEATURE_FLAG_AI_SHELL_MODEL_SWITCHER,
  isFeatureEnabled,
} from '@captivateiq/assistant/utils/featureFlags';
import { ModelSelector } from '@captivateiq/assistant/components/ModelSelector';

import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';

// Design system tokens — packages/quotient/src/theme/foundations/colors/colors.ts
const POSITIVE_BG = '#D5FCE2'; // positive.50
const POSITIVE_BORDER = '#008937'; // positive.600
const DANGER_BG = '#FFE3E3'; // danger.50
const DANGER_BORDER = '#E32323'; // danger.600
const CHIP_UNFILLED_BG = '#E8EEF4'; // primaryNeutral.200

type AttachmentStatus = 'uploading' | 'done' | 'error';

type PendingAttachmentState = {
  localId: string;
  fileName: string;
  progress: number;
  status: AttachmentStatus;
  meta?: AttachmentMeta;
};

function chipSx(attachment: PendingAttachmentState): SxProps<Theme> {
  const bottomBar = {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: '2px',
  };

  switch (attachment.status) {
    case 'done':
      return {
        backgroundColor: POSITIVE_BG,
        '&::after': { ...bottomBar, width: '100%', backgroundColor: POSITIVE_BORDER },
      };
    case 'error':
      return {
        backgroundColor: DANGER_BG,
        '&::after': { ...bottomBar, width: '100%', backgroundColor: DANGER_BORDER },
      };
    case 'uploading':
      return {
        background: `linear-gradient(to right, ${POSITIVE_BG} ${attachment.progress}%, ${CHIP_UNFILLED_BG} ${attachment.progress}%)`,
        '&::after': { ...bottomBar, width: `${attachment.progress}%`, backgroundColor: POSITIVE_BORDER },
      };
    default:
      return {};
  }
}

type MessageInputProps = {
  onSubmit: (message: string, attachments?: AttachmentMeta[]) => Promise<void>;
  agentThinking?: boolean;
};

const MAX_TEXTAREA_ROWS = 5;
const ACCEPTED_FILE_TYPES = '.csv,.pdf';
const MAX_ATTACHMENT_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB — must match backend MAX_ATTACHMENT_SIZE_BYTES
const MAX_ATTACHMENTS = 5;

export const MessageInput = ({ onSubmit, agentThinking = false }: MessageInputProps) => {
  const { t } = useTranslation('assistant');
  const { isExpanded, createAttachment, selectedAgent, selectedModel, setSelectedModel } = useAIShellContext();
  const supportsAttachments =
    selectedAgent === agentIds.COMP_BUILDER_AGENT && isFeatureEnabled(FEATURE_FLAG_AI_SHELL_FILE_UPLOAD);
  const showModelSwitcher = isFeatureEnabled(FEATURE_FLAG_AI_SHELL_MODEL_SWITCHER);

  const [content, setContent] = useState<string>('');
  const [isMultiline, setIsMultiline] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachmentState[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const singleLineHeightRef = useRef(0);

  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  // Capture the textarea's 1-line height once after mount so we can compare later.
  useLayoutEffect(() => {
    if (textRef.current) {
      singleLineHeightRef.current = textRef.current.offsetHeight;
    }
  }, []);

  // After each content change, check whether the textarea grew past its 1-line height.
  // We only switch TO multiline automatically; switching back to single-line only happens
  // when content is cleared (on submit or full deletion) to avoid layout oscillation at
  // the exact wrap boundary when the column mode gives the textarea extra width.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || !singleLineHeightRef.current) return;
    if (el.offsetHeight > singleLineHeightRef.current) {
      setIsMultiline(true);
    }
  }, [content]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setContent(value);
    if (!value) setIsMultiline(false);
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (!files.length) return;

      // Reset so the same file(s) can be re-selected after dismissal
      const input = event.target as HTMLInputElement;
      input.value = '';

      const slotsAvailable = MAX_ATTACHMENTS - pendingAttachments.filter((a) => a.status !== 'error').length;
      const errors: string[] = [];

      if (files.length > slotsAvailable) {
        errors.push(`Only ${slotsAvailable} more file${slotsAvailable === 1 ? '' : 's'} can be added.`);
      }

      const validations = await Promise.all(
        files.slice(0, slotsAvailable).map(async (file) => {
          if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
            window.DD_RUM?.addError('AI shell file rejected — too large', {
              size: file.size,
              limit: MAX_ATTACHMENT_SIZE_BYTES,
            });
            return { file, error: `${file.name}: too large (max ${MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)} MB).` };
          }
          const typeError = await validateFileType(file);
          if (typeError) {
            window.DD_RUM?.addError('AI shell file rejected — invalid type', {
              type: file.type,
              name: file.name,
            });
            return { file, error: `${file.name}: ${typeError}` };
          }
          return { file, error: null };
        }),
      );

      for (const { file, error } of validations) {
        if (error !== null) {
          errors.push(error);
        } else {
          const localId = crypto.randomUUID();
          setPendingAttachments((prev) => [
            ...prev,
            { localId, fileName: file.name, progress: 0, status: 'uploading' },
          ]);

          const controller = new AbortController();
          abortControllersRef.current.set(localId, controller);

          createAttachment(
            file,
            (pct) =>
              setPendingAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, progress: pct } : a))),
            controller.signal,
          )
            .then((meta) => {
              setPendingAttachments((prev) =>
                prev.map((a) => (a.localId === localId ? { ...a, status: 'done', progress: 100, meta } : a)),
              );
            })
            .catch((err: unknown) => {
              if (err instanceof DOMException && err.name === 'AbortError') return;
              window.DD_RUM?.addError('AI shell file upload failed', { name: file.name });
              setPendingAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, status: 'error' } : a)));
            })
            .finally(() => {
              abortControllersRef.current.delete(localId);
            });
        }
      }

      setAttachmentError(errors.length > 0 ? errors.join(' ') : null);
    },
    [createAttachment, pendingAttachments],
  );

  const handleDismissAttachment = useCallback((localId: string) => {
    abortControllersRef.current.get(localId)?.abort();
    setPendingAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (agentThinking || content.trim() === '' || pendingAttachments.some((a) => a.status === 'uploading')) {
        return;
      }
      const attachments = pendingAttachments.flatMap((a) => (a.status === 'done' && a.meta ? [a.meta] : []));
      setPendingAttachments([]);
      onSubmit(content, attachments);
      setContent('');
      setIsMultiline(false);
    },
    [onSubmit, setContent, content, agentThinking, pendingAttachments],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !agentThinking && content.trim() !== '') {
        event.preventDefault();
        handleSubmit(event);
      }
    },
    [handleSubmit, agentThinking, content],
  );

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const hasUploadInProgress = pendingAttachments.some((a) => a.status === 'uploading');
  const activeAttachmentCount = pendingAttachments.filter((a) => a.status !== 'error').length;

  return (
    <QBox sx={expandedTextColumnSx(isExpanded)}>
      {showModelSwitcher && (
        <QBox sx={{ paddingBottom: '6px' }}>
          <ModelSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />
        </QBox>
      )}
      <form onSubmit={handleSubmit}>
        {attachmentError && (
          <QBox sx={{ paddingBottom: '4px', color: 'error.main', fontSize: '12px' }}>{attachmentError}</QBox>
        )}
        {pendingAttachments.length > 0 && (
          <QBox sx={{ paddingBottom: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {pendingAttachments.map((attachment) => (
              <QChip
                data-testid={`attachment-chip-${attachment.status}`}
                deleteIcon={<QIcon data-testid="attachment-chip-dismiss" icon={faTimes} sx={{ fontSize: '12px' }} />}
                key={attachment.localId}
                label={attachment.fileName}
                size="small"
                sx={chipSx(attachment)}
                onDelete={() => handleDismissAttachment(attachment.localId)}
              />
            ))}
          </QBox>
        )}
        <QBox
          sx={{
            borderRadius: '4px',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: isMultiline ? 'column' : 'row',
            alignItems: isMultiline ? 'stretch' : 'center',
            '&:focus-within': {
              borderColor: 'primary.main',
            },
          }}
        >
          {supportsAttachments && (
            <input
              accept={ACCEPTED_FILE_TYPES}
              data-testid="file-upload-input"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              type="file"
              onChange={handleFileChange}
            />
          )}
          <QTextField
            data-testid="ai-message-input"
            inputRef={textRef}
            maxRows={MAX_TEXTAREA_ROWS}
            multiline
            optional
            placeholder={t('input_placeholders.message') as string}
            sx={{
              ...(isMultiline ? {} : { flex: 1 }),
              padding: 0,
              '& .MuiInputBase-root': { border: 'none' },
              '& .MuiInputBase-root.Mui-focused': { outline: 'none' },
            }}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <QBox
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '4px',
              padding: isMultiline ? '0 6px 6px' : '6px',
              flexShrink: 0,
            }}
          >
            {supportsAttachments && (
              <QButton
                data-testid="ai-attach-button"
                disabled={agentThinking || activeAttachmentCount >= MAX_ATTACHMENTS}
                sx={{
                  minWidth: 'unset',
                  padding: '4px',
                  borderRadius: '4px',
                  height: '24px',
                  width: '24px',
                }}
                type="button"
                variant="text"
                onClick={handleAttachClick}
              >
                <QIcon icon={faPaperclip} sx={{ width: '10.9px', height: '12.5px' }} />
              </QButton>
            )}
            <QButton
              data-testid="ai-send-button"
              disabled={hasUploadInProgress}
              size="small"
              sx={{
                minWidth: 'unset',
                padding: '4px 4px 4px 4px',
                borderRadius: '4px',
                height: '24px',
                width: '24px',
              }}
              type="submit"
              variant={content && !agentThinking ? 'contained' : 'outlined'}
            >
              <QIcon icon={agentThinking ? faCircleStop : faArrowUp} sx={{ width: '9.6px', height: '11.2px' }} />
            </QButton>
          </QBox>
        </QBox>
      </form>
    </QBox>
  );
};
